/**
 * IndexedDB 工具类，用于管理物流会话存储
 */
class LogisticsConversationStorage {
  constructor() {
    this.dbName = 'AI_Merchant_Logistics_DB';
    this.dbVersion = 1;
    this.storeName = 'logistics_conversations';
    this.db = null;
  }

  /**
   * 初始化数据库
   * @returns {Promise<IDBDatabase>} 数据库实例
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('IndexedDB open error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // 创建物流会话存储对象
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
          // 创建索引以支持按时间排序
          objectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }
      };
    });
  }

  /**
   * 获取所有物流会话
   * @returns {Promise<Array>} 会话列表
   */
  async getAllConversations() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.getAll();

        request.onerror = (event) => {
          console.error('Failed to get logistics conversations:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          const conversations = event.target.result || [];
          // 按更新时间降序排序
          conversations.sort((a, b) => b.updatedAt - a.updatedAt);
          resolve(conversations);
        };
      });
    } catch (error) {
      console.error('Error getting logistics conversations:', error);
      return [];
    }
  }

  /**
   * 保存单个物流会话
   * @param {Object} conversation 会话对象
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversation(conversation) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.put(conversation);

        request.onerror = (event) => {
          console.error('Failed to save logistics conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error saving logistics conversation:', error);
      return false;
    }
  }

  /**
   * 保存多个物流会话
   * @param {Array} conversations 会话数组
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversations(conversations) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        
        let completed = 0;
        let hasError = false;
        
        // 清空现有数据
        const clearRequest = objectStore.clear();
        
        clearRequest.onerror = (event) => {
          console.error('Failed to clear logistics conversations:', event.target.error);
          reject(event.target.error);
        };
        
        clearRequest.onsuccess = () => {
          // 添加新数据
          conversations.forEach(conversation => {
            const request = objectStore.add(conversation);
            
            request.onerror = (event) => {
              console.error('Failed to add logistics conversation:', event.target.error);
              hasError = true;
            };
            
            request.onsuccess = () => {
              completed++;
              if (completed === conversations.length) {
                resolve(!hasError);
              }
            };
          });
        };
      });
    } catch (error) {
      console.error('Error saving logistics conversations:', error);
      return false;
    }
  }

  /**
   * 获取单个物流会话
   * @param {string} conversationId 会话ID
   * @returns {Promise<Object|null>} 会话对象或null
   */
  async getConversation(conversationId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.get(conversationId);

        request.onerror = (event) => {
          console.error('Failed to get logistics conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          resolve(event.target.result || null);
        };
      });
    } catch (error) {
      console.error('Error getting logistics conversation:', error);
      return null;
    }
  }

  /**
   * 删除物流会话
   * @param {string} conversationId 会话ID
   * @returns {Promise<boolean>} 是否删除成功
   */
  async deleteConversation(conversationId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.delete(conversationId);

        request.onerror = (event) => {
          console.error('Failed to delete logistics conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error deleting logistics conversation:', error);
      return false;
    }
  }

  /**
   * 清空所有物流会话
   * @returns {Promise<boolean>} 是否清空成功
   */
  async clearAllConversations() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.clear();

        request.onerror = (event) => {
          console.error('Failed to clear logistics conversations:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error clearing logistics conversations:', error);
      return false;
    }
  }
}

// 创建单例实例
const logisticsConversationStorage = new LogisticsConversationStorage();

export default logisticsConversationStorage;
